// ============================================================
// Notifications page — full-page browse + manage view.
//
// Sits alongside the bell-icon dropdown in the header. The dropdown
// is for "quick glance, mark a couple read" while this page is for
// "I want to actually work through what's pending". It carries:
//
//   * Tab strip across the top: All / Unread / Tasks / Leads /
//     Meetings / SLA / Other (counts shown per tab).
//   * Date range pickers (From / To) so a user can audit history.
//   * Per-row actions: Open lead / Mark read / Delete.
//   * Bulk: Mark all read.
//
// Backend already exposes everything we need — see
// `routes/notification_routes.py`:
//   GET    /api/notifications           list, with date_from/date_to
//   POST   /api/notifications/{id}/read mark single read
//   POST   /api/notifications/mark-all-read
//   DELETE /api/notifications/{id}      delete one
//
// Why a separate page rather than expanding the dropdown:
// the dropdown is z-index-anchored to the bell and capped at ~96
// rows by ScrollArea. A full-page view gives breathing room for
// long messages (Lead inactivity messages now carry stage tags and
// "since {date}" phrasing), and lets us pivot by tab without
// fighting the popover positioning.
// ============================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Bell, CheckCheck, Trash2, ExternalLink, Inbox,
  Briefcase, Users, Calendar, AlertTriangle, MessageSquare, FileText,
  RefreshCw,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import InfoBadge from '../components/InfoBadge';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// --- Tab definitions ------------------------------------------------
// Each tab maps to a predicate over the notification's `type` field.
// The "All" tab is the union, "Other" is the catch-all so nothing
// falls off the page. Counts are computed client-side from the
// already-fetched list (we always pull a large window) — saves a
// round-trip per tab click.
const TABS = [
  { key: 'all',     label: 'All',      icon: Inbox,         match: () => true },
  { key: 'unread',  label: 'Unread',   icon: Bell,          match: (n) => !n.is_read },
  { key: 'leads',   label: 'Leads',    icon: Users,         match: (n) => /lead|contact|inactivity|reopened|sla/i.test(n.type) },
  { key: 'tasks',   label: 'Tasks',    icon: Briefcase,     match: (n) => /task|followup|reminder/i.test(n.type) },
  { key: 'meetings',label: 'Meetings', icon: Calendar,      match: (n) => /meeting/i.test(n.type) },
  { key: 'alerts',  label: 'Alerts',   icon: AlertTriangle, match: (n) => /breach|warning|escalation|approval/i.test(n.type) },
  { key: 'other',   label: 'Other',    icon: FileText,      match: (n) => true /* fallback handled in render */ },
];

// Type → (icon, tone) lookup. Tone drives the small coloured dot on
// the left rail of each row, so a user scanning the list can pick
// out "task due" vs "lead inactive" at a glance.
const TYPE_META = {
  // Lead lifecycle
  lead_inactivity:  { icon: Users,        tone: 'amber'   },
  lead_reopened:    { icon: RefreshCw,    tone: 'sky'     },
  lead_assigned:    { icon: Users,        tone: 'violet'  },
  lead_reassigned:  { icon: Users,        tone: 'violet'  },
  // Tasks
  task_due:         { icon: Briefcase,    tone: 'rose'    },
  task_created:     { icon: Briefcase,    tone: 'emerald' },
  task_assigned:    { icon: Briefcase,    tone: 'blue'    },
  // Follow-ups
  followup_due:           { icon: Bell,   tone: 'amber'   },
  followup_pre_warning:   { icon: Bell,   tone: 'sky'     },
  followup_still_pending: { icon: Bell,   tone: 'rose'    },
  // Meetings
  meeting_reminder: { icon: Calendar,     tone: 'violet'  },
  // SLA
  sla_warning:      { icon: AlertTriangle, tone: 'amber'  },
  sla_breach:       { icon: AlertTriangle, tone: 'red'    },
  // Comments / notes
  comment:          { icon: MessageSquare, tone: 'blue'   },
  note:             { icon: MessageSquare, tone: 'blue'   },
  // Default
  _default:         { icon: Bell,         tone: 'slate'   },
};

// Tone → tailwind classes. Kept centralised so adding a new tone is
// one row, not a sprinkle across the file.
const TONE = {
  amber:   { dot: 'bg-amber-400',   ring: 'ring-amber-100',   text: 'text-amber-700'  },
  rose:    { dot: 'bg-rose-400',    ring: 'ring-rose-100',    text: 'text-rose-700'   },
  emerald: { dot: 'bg-emerald-400', ring: 'ring-emerald-100', text: 'text-emerald-700'},
  blue:    { dot: 'bg-blue-400',    ring: 'ring-blue-100',    text: 'text-blue-700'   },
  sky:     { dot: 'bg-sky-400',     ring: 'ring-sky-100',     text: 'text-sky-700'    },
  violet:  { dot: 'bg-violet-400',  ring: 'ring-violet-100',  text: 'text-violet-700' },
  red:     { dot: 'bg-red-500',     ring: 'ring-red-100',     text: 'text-red-700'    },
  slate:   { dot: 'bg-slate-400',   ring: 'ring-slate-100',   text: 'text-slate-700'  },
};

// Priority badge styling. Mirrors what the bell uses so the page
// reads consistently with the dropdown.
const PRIORITY_BADGE = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  medium:   'bg-amber-100 text-amber-700 border-amber-200',
  low:      'bg-slate-100 text-slate-600 border-slate-200',
};

function metaFor(notification) {
  return TYPE_META[notification.type] || TYPE_META._default;
}

function relativeTime(s) {
  if (!s) return '';
  try {
    return formatDistanceToNow(new Date(s), { addSuffix: true });
  } catch {
    return '';
  }
}

function absoluteTime(s) {
  if (!s) return '';
  try {
    return format(new Date(s), 'EEE, d MMM yyyy · HH:mm');
  } catch {
    return s;
  }
}

// --- Page component -------------------------------------------------
export default function Notifications() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Pull the whole window the user is interested in. We ask for a
  // generous limit (500) so all tab counts are accurate without
  // re-fetching on tab change — the filtering happens client-side
  // because the type → tab mapping is regex-based and changes
  // with the categories above.
  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const params = { token, limit: 500 };
      if (from) params.date_from = from;
      if (to)   params.date_to   = to;
      const r = await axios.get(`${API}/api/notifications`, { params });
      setRows(Array.isArray(r.data?.notifications) ? r.data.notifications : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, from, to]);

  useEffect(() => { load(); }, [load]);

  // Compute per-tab counts from the loaded list. The catch-all
  // "Other" tab is everything that didn't match any of the named
  // tabs (so we don't double-count rows that hit multiple regexes —
  // each row goes into the FIRST matching tab, not all of them).
  const { counts, currentRows } = useMemo(() => {
    const buckets = Object.fromEntries(TABS.map((t) => [t.key, []]));
    for (const n of rows) {
      buckets.all.push(n);
      if (!n.is_read) buckets.unread.push(n);
      // Find first named tab (excluding all/unread/other) that matches
      let placed = false;
      for (const t of TABS) {
        if (['all', 'unread', 'other'].includes(t.key)) continue;
        if (t.match(n)) { buckets[t.key].push(n); placed = true; break; }
      }
      if (!placed) buckets.other.push(n);
    }
    return {
      counts: Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, v.length])),
      currentRows: buckets[tab] || [],
    };
  }, [rows, tab]);

  // --- Actions -----------------------------------------------------
  const markRead = async (id) => {
    // Optimistic update — flip the flag in-place so the row visually
    // settles immediately. If the request fails we revert + toast.
    const prev = rows;
    setRows((r) => r.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await axios.post(`${API}/api/notifications/${id}/read`, null, { params: { token } });
    } catch (e) {
      setRows(prev);
      toast.error('Could not mark as read');
    }
  };

  const markAllRead = async () => {
    const prev = rows;
    setRows((r) => r.map((n) => ({ ...n, is_read: true })));
    try {
      const r = await axios.post(`${API}/api/notifications/mark-all-read`, null, { params: { token } });
      toast.success(`Marked ${r.data?.marked_count ?? 'all'} notification${r.data?.marked_count === 1 ? '' : 's'} as read`);
    } catch (e) {
      setRows(prev);
      toast.error('Could not mark all as read');
    }
  };

  const remove = async (id) => {
    const prev = rows;
    setRows((r) => r.filter((n) => n.id !== id));
    try {
      await axios.delete(`${API}/api/notifications/${id}`, { params: { token } });
    } catch (e) {
      setRows(prev);
      toast.error('Could not delete');
    }
  };

  // Open the lead/task/meeting linked to this notification. We use
  // related_type to decide where to navigate. Falls back to no-op
  // (with a toast) when there's no link to follow.
  const openLinked = (n) => {
    const rid = n.related_id;
    if (!rid) {
      toast.info('Nothing to open — this notification has no linked record');
      return;
    }
    // Mark as read in the background so the row stops glowing once
    // the user navigates back. Best-effort — we don't block the nav.
    if (!n.is_read) markRead(n.id);
    switch (n.related_type) {
      case 'contact':
        navigate(`/contacts?lead=${encodeURIComponent(rid)}`);
        break;
      case 'task':
        navigate(`/my-dashboard?task=${encodeURIComponent(rid)}`);
        break;
      case 'meeting':
        navigate(`/meeting-intelligence?id=${encodeURIComponent(rid)}`);
        break;
      case 'deal':
        navigate(`/deals?id=${encodeURIComponent(rid)}`);
        break;
      default:
        // No specific surface — drop them on the lead dashboard which
        // is the most common destination.
        navigate(`/contacts?lead=${encodeURIComponent(rid)}`);
    }
  };

  // --- Render -------------------------------------------------------
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            Notifications
            <InfoBadge text="Every alert that lands in your bell icon also shows up here. Use the tabs to slice by category and the date range to audit history. Mark items read or delete the ones you don't care about." />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {loading
              ? 'Loading…'
              : `${rows.length} notification${rows.length === 1 ? '' : 's'} in view`
            }
            {counts.unread > 0 && !loading && (
              <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-rose-600">
                · {counts.unread} unread
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            disabled={refreshing}
            className="gap-1.5"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={markAllRead}
            disabled={counts.unread === 0}
            className="gap-1.5"
          >
            <CheckCheck size={14} />
            Mark all read
          </Button>
        </div>
      </div>

      {/* Date range + tab strip */}
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-[11px] font-medium text-slate-500 block mb-1">From</label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="h-8 w-[150px] text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-slate-500 block mb-1">To</label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-8 w-[150px] text-sm"
              />
            </div>
            {(from || to) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setFrom(''); setTo(''); }}
                className="text-xs text-slate-500"
              >
                Clear date range
              </Button>
            )}
          </div>
        </CardHeader>
        <div className="px-4 pb-2 border-t border-slate-100">
          <div className="flex items-center gap-0.5 overflow-x-auto pt-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              const count = counts[t.key] || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm
                              font-medium transition shrink-0 ${
                    active
                      ? 'text-violet-700 bg-violet-50'
                      : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                  <span className={`text-[10px] tabular-nums ${
                    active ? 'text-violet-500' : 'text-slate-400'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* List body */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : currentRows.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                <Bell size={20} className="text-slate-300" />
              </div>
              <p className="text-sm font-semibold text-slate-700">All caught up</p>
              <p className="text-xs text-slate-500 mt-1">
                No {tab === 'all' ? '' : tab + ' '}notifications
                {(from || to) ? ' in this date range' : ''}.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {currentRows.map((n) => {
                const meta = metaFor(n);
                const tone = TONE[meta.tone] || TONE.slate;
                const Icon = meta.icon;
                return (
                  <div
                    key={n.id}
                    className={`group flex gap-3 p-4 transition cursor-pointer hover:bg-slate-50 ${
                      !n.is_read ? 'bg-violet-50/40' : ''
                    }`}
                    onClick={() => openLinked(n)}
                  >
                    {/* Unread dot rail */}
                    <div className="shrink-0 pt-1 w-2 flex justify-center">
                      {!n.is_read ? (
                        <div className={`h-2 w-2 rounded-full ${tone.dot}`} />
                      ) : (
                        <div className="h-2 w-2" />
                      )}
                    </div>
                    {/* Icon */}
                    <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center
                                     bg-white ring-1 ${tone.ring}`}>
                      <Icon size={16} className={tone.text} />
                    </div>
                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`text-sm ${!n.is_read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                            {n.title}
                          </p>
                          <p className="text-xs text-slate-600 mt-0.5 break-words">
                            {n.message}
                          </p>
                          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-2">
                            <span title={absoluteTime(n.created_at)}>
                              {relativeTime(n.created_at)}
                            </span>
                            {n.priority && n.priority !== 'medium' && (
                              <Badge
                                variant="outline"
                                className={`h-4 px-1.5 text-[10px] font-medium border ${
                                  PRIORITY_BADGE[n.priority] || PRIORITY_BADGE.medium
                                }`}
                              >
                                {n.priority}
                              </Badge>
                            )}
                            {n.category && (
                              <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                                {n.category}
                              </span>
                            )}
                          </p>
                        </div>
                        {/* Action cluster — only visible on hover so the
                            row stays clean at rest. The actions stop
                            propagation so the outer row's openLinked
                            doesn't double-fire when the user clicks
                            "Mark read" or "Delete". */}
                        <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                          {n.related_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={(e) => { e.stopPropagation(); openLinked(n); }}
                              title="Open linked record"
                            >
                              <ExternalLink size={12} />
                            </Button>
                          )}
                          {!n.is_read && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                              title="Mark as read"
                            >
                              <CheckCheck size={12} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
